/**
 * Welche Anwendungen dem angemeldeten Nutzer freigeschaltet sind (#504).
 *
 * `allowed_applications` ist die zentrale Zuweisung aus Cortex — vier Werte,
 * die das BFF akzeptiert: `webos`, `pdb`, `editor`, `composer`. Sie entscheidet
 * hier, welche Routen ein Nutzer überhaupt betreten darf:
 *
 *   webos → der Desktop (`/desktop`)
 *   pdb   → die shell-freien Routen (`/chat`, `/drive`, `/settings`)
 *
 * Der Wert wird einmal geladen und in einem Modul-Cache gehalten: Gate,
 * Login-Weiche und Rail fragen ihn im selben Seitenaufbau, und drei Anfragen an
 * `/users/me` für dieselbe Antwort wären Verschwendung. Der Cache lebt nur so
 * lange wie der Tab; nach einer Freischaltung in Cortex reicht ein Neuladen.
 */
import { useEffect, useState } from 'react'
import { backendUserProfilesService } from '@/services/backend-user-profiles-service'
import { logError } from '@/lib/logger'

export type ApplicationId = 'webos' | 'pdb' | 'editor' | 'composer'

/** Route-Zugang: welche Anwendung eine Route voraussetzt. */
export const DESKTOP_APPLICATION: ApplicationId = 'webos'
export const STANDALONE_APPLICATION: ApplicationId = 'pdb'

let cache: Promise<string[]> | null = null

function loadAllowedApplications(): Promise<string[]> {
  if (!cache) {
    cache = backendUserProfilesService
      .getMyProfile()
      .then((profile) => profile?.allowed_applications ?? [])
      .catch((error) => {
        logError(error as Error, { context: 'useAllowedApplications' })
        cache = null // ein Fehlschlag darf sich nicht festsetzen
        return []
      })
  }
  return cache
}

/** Nach An-/Abmeldung verwerfen — sonst sähe der nächste Nutzer fremde Rechte. */
export function clearAllowedApplicationsCache(): void {
  cache = null
}

export function useAllowedApplications() {
  const [apps, setApps] = useState<string[] | null>(null)

  useEffect(() => {
    let active = true
    void loadAllowedApplications().then((loaded) => {
      if (active) setApps(loaded)
    })
    return () => {
      active = false
    }
  }, [])

  return {
    /** `null`, solange noch geladen wird — nicht mit "keine Rechte" verwechseln. */
    apps,
    isLoading: apps === null,
    has: (app: ApplicationId) => apps?.includes(app) ?? false,
  }
}

/** Für Stellen außerhalb von React (Login-Weiche). */
export async function fetchAllowedApplications(): Promise<string[]> {
  return loadAllowedApplications()
}
